import { PropsWithChildren } from "react"

import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons"
import {
  Box,
  Container,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material"
import { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import { BackButton } from "@/components"

import PageContainerStepper from "./components/PageContainerStepper"

type PageContainerProps = {
  headline: string
  headlineLoading?: boolean
  leftIcon?: React.ReactNode
  activeStep?: number
  onBackClick?: () => void
  isTicketProductType?: boolean
  isSummer?: boolean
}

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up("sm")]: {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}))

const StyledHeadline = styled(Typography)<{ component?: React.ElementType }>({
  display: "flex",
  alignItems: "center",
})

const StyledIconBox = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1),
  fontSize: "1.5rem",
  lineHeight: 0,
  marginTop: "-2px",
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
    marginTop: "-6px",
  },
}))

const PageContainer = ({
  headline,
  headlineLoading,
  onBackClick,
  activeStep,
  leftIcon,
  isTicketProductType = false,
  isSummer = false,
  children,
}: PropsWithChildren<PageContainerProps>) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  const shouldShowStepper = activeStep !== undefined
  const shouldShowBackButton = onBackClick !== undefined
  return (
    <StyledContainer id="page-container-stack">
      <Stack spacing={2} alignItems="flex-start" sx={{ mb: { xs: 2, md: 3 } }}>
        {shouldShowBackButton && (
          <BackButton
            onClick={onBackClick}
            startIcon={
              <FaIcon
                icon={faChevronLeft}
                style={{ fontSize: "1rem" }}
                aria-hidden="true"
              />
            }
            data-testid="back-button"
          >
            {t("backButtonLabel")}
          </BackButton>
        )}
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          width="100%"
        >
          {!isDesktop && shouldShowStepper && (
            <PageContainerStepper
              activeStep={activeStep}
              isTicketProductType={isTicketProductType}
              isSummer={isSummer}
            />
          )}
          <StyledHeadline variant={isDesktop ? "h4" : "h5"} component="h1">
            {leftIcon && <StyledIconBox>{leftIcon}</StyledIconBox>}
            {headlineLoading ? (
              <Skeleton
                variant="text"
                width={250}
                sx={{ display: "inline-block" }}
              />
            ) : (
              headline
            )}
          </StyledHeadline>
          {isDesktop && shouldShowStepper && (
            <div>
              {/* Margin right is removed by stack */}
              <PageContainerStepper
                activeStep={activeStep}
                isTicketProductType={isTicketProductType}
                isSummer={isSummer}
              />
            </div>
          )}
        </Stack>
      </Stack>
      {children}
    </StyledContainer>
  )
}

export default PageContainer
